/**
 * DV-TEMP-004: Simplified Execution Events Hook
 *
 * Simplified version with only valid EventType enum values
 * and corrected WebSocket integration.
 */

import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSecureWebSocket } from './useSecureWebSocket';
import { buildApiUrl, getApiHeadersGet } from '@/lib/api-base';
import { API_ENDPOINTS } from '@/lib/api-base';
import type {
  TimelineEvent,
  TimelineEventsResponse,
  UseTimelineOptions
} from '@/types/execution-events';
import type { EventType } from '@/types/events-v2';
import type { StageName } from '@/lib/shared-types';

// Helper function to validate UUID format
function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

// Utility function for file size formatting (currently unused)
// function formatFileSize(bytes: number): string {
//   if (bytes === 0) return '0 B';
//   const k = 1024;
//   const sizes = ['B', 'KB', 'MB', 'GB'];
//   const i = Math.floor(Math.log(bytes) / Math.log(k));
//   return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
// }

export interface UseExecutionEventsOptions extends UseTimelineOptions {
  executionId: string;
  enableLiveUpdates?: boolean;
}

export function useExecutionEvents(options: UseExecutionEventsOptions) {
  const {
    executionId,
    stage,
    eventType,
    persona,
    timeRange,
    enabled = true,
    refetchInterval,
    enableLiveUpdates = false
  } = options;

  // Track component mount state to prevent polling after unmount
  const isMountedRef = useRef(true);
  const [isComponentEnabled, setIsComponentEnabled] = useState(enabled);

  useEffect(() => {
    setIsComponentEnabled(enabled);
    return () => {
      isMountedRef.current = false;
      setIsComponentEnabled(false);
    };
  }, [enabled]);

  // const queryClient = useQueryClient(); // Unused for now

  // WebSocket integration for live updates
  const wsState = useSecureWebSocket({
    executionId,
    subscriptions: {
      executions: [executionId],
      stages: [], // Subscribe to all stages
      eventTypes: ['timeline:event_added', 'timeline:events_batch', 'timeline:event_updated', 'execution:progress', 'execution:stage_completed', 'execution:failed']
    },
    onConnectionChange: useCallback((status: any) => {
      // Log connection status changes for debugging and status reports
      console.debug(`📡 WebSocket connection status changed: ${status}`);

      // Track last WebSocket hint time for status reports
      if (status === 'connected') {
        localStorage.setItem('last_ws_hint', new Date().toISOString());
      }
    }, []),
    enablePollingFallback: enableLiveUpdates
  });

  // Transform raw event data to TimelineEvent
  const transformEvent = useCallback((event: any): TimelineEvent => {
    const getDisplayTitle = (): string => {
      switch (event.event_type as EventType) {
        // Stage lifecycle events (valid EventType)
        case 'stage_started':
          return `${event.stage} started`;
        case 'stage_completed':
          return `${event.stage} completed`;
        case 'stage_failed':
          return `${event.stage} failed`;

        // Pipeline lifecycle events (valid EventType)
        case 'pipeline_started':
          return 'Pipeline execution started';
        case 'pipeline_completed':
          return 'Pipeline execution completed';

        // QC and quality events (valid EventType)
        case 'qc_started':
          return `QC started for ${event.payload?.target_stage || event.stage}`;
        case 'qc_completed':
          return `QC completed for ${event.payload?.target_stage || event.stage}`;
        case 'qc_failed':
          return `QC failed for ${event.payload?.target_stage || event.stage}`;
        case 'scores_ready':
          return `QC scores for ${event.payload?.target_stage}`;

        // Content and output events (valid EventType)
        case 'content_ready':
          return `${event.stage} content ready`;

        // Billing and cost events (valid EventType)
        case 'billing_committed':
          return `Cost committed: $${event.payload?.costUsd || 0}`;
        case 'billing_record_created':
          return 'Billing record created';

        // Export and publishing events (valid EventType)
        case 'export_preview':
          return 'Export preview ready';

        // Error and processing events (valid EventType)
        case 'error':
          return `Error: ${event.payload?.error_type || 'Unknown error'}`;
        case 'processing':
          return `Processing: ${event.payload?.status || 'In progress'}`;
        case 'quality_warning':
          return `Quality warning: ${event.payload?.warning || 'Quality below threshold'}`;

        default:
          // Graceful fallback for unknown event types
          return event.event_type.replace(/[._-]/g, ' ').toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase());
      }
    };

    const getDescription = (): string | undefined => {
      switch (event.event_type as EventType) {
        // Error events
        case 'stage_failed':
        case 'error':
          return `Error: ${event.payload?.error || event.payload?.error_message || 'Unknown error'}`;

        // Content and artifact events
        case 'content_ready':
          const files = event.payload?.files || [];
          const duration = event.payload?.durationMs;
          const durationText = duration ? ` in ${Math.round(duration / 1000)}s` : '';
          return `${files.length} files generated${durationText}`;

        // QC and quality events
        case 'qc_completed':
        case 'scores_ready': {
          const score = Number(event.payload?.weighted_score);
          const passed = event.payload?.passed;
          if (Number.isFinite(score) && typeof passed === 'boolean') {
            return `Score: ${(score * 100).toFixed(1)}% (${passed ? 'PASS' : 'FAIL'})`;
          }
          return 'QC score available';
        }
        case 'quality_warning':
          return `Score: ${((event.payload?.quality_score || 0) * 100).toFixed(1)}% below threshold`;

        // Billing and cost events
        case 'billing_committed':
        case 'billing_record_created':
          const provider = event.payload?.provider || 'unknown';
          const model = event.payload?.model || 'unknown';
          const cost = event.payload?.costUsd || event.payload?.cost_usd;
          return cost ? `$${cost.toFixed(4)} • ${provider}/${model}` : `${provider}/${model}`;

        default:
          return undefined;
      }
    };

    const getSeverity = (): TimelineEvent['severity'] => {
      switch (event.event_type as EventType) {
        // Error events - high severity
        case 'stage_failed':
        case 'error':
          return 'error';

        // Warning events - medium severity
        case 'quality_warning':
          return 'warning';

        // Success events - positive outcome
        case 'stage_completed':
        case 'pipeline_completed':
        case 'qc_completed':
        case 'content_ready':
        case 'export_preview':
          return 'success';

        // QC results - conditional severity
        case 'scores_ready':
          if ('payload' in event && 'passed' in event.payload) {
            return event.payload.passed ? 'success' : 'warning';
          }
          return 'info';

        case 'qc_failed':
          return 'error';

        // Informational events - low severity
        case 'stage_started':
        case 'qc_started':
        case 'processing':
        case 'billing_committed':
        case 'billing_record_created':
          return 'info';

        default:
          return 'info';
      }
    };

    const getArtifactRefs = () => {
      const refs = event.payload?.refs || event.payload?.files || [];
      if (Array.isArray(refs)) {
        return refs.map((ref: any, index: number) => ({
          id: typeof ref === 'string' ? ref : ref.id || `artifact-${index}`,
          name: typeof ref === 'string' ? ref.split('/').pop() || ref : ref.name || `Artifact ${index + 1}`,
          type: 'other' as const,
          storageUri: typeof ref === 'string' ? ref : ref.storage_uri || ref.url || '',
          publicUrl: typeof ref === 'string' ? ref : ref.public_url || ref.url || ''
        }));
      }
      return [];
    };

    return {
      id: event.id || `${event.execution_id}-${event.timestamp}-${event.event_type}`,
      event_type: event.event_type as EventType,
      stage: event.stage as StageName,
      execution_id: event.execution_id,
      timestamp: event.timestamp,
      payload: event.payload,
      correlation_id: event.correlation_id,
      workflow_run_id: event.workflow_run_id,
      displayTitle: getDisplayTitle(),
      displayDescription: getDescription(),
      artifactRefs: getArtifactRefs(),
      severity: getSeverity(),
      isExpandable: Boolean(event.payload && Object.keys(event.payload).length > 0),
      attemptNumber: event.payload?.attempt || event.payload?.attemptNumber
    };
  }, []);

  // Fetch timeline events with pagination
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['timeline', executionId, 'filtered', { stage, eventType, persona, timeRange }],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const filters = { stage, eventType, persona, timeRange };
      const endpoint = API_ENDPOINTS.demo.temporalEvents(executionId, 1, filters);
      const url = buildApiUrl(endpoint) + (pageParam ? `&cursor=${pageParam}` : '');

      // Generate correlation ID for request tracking
      const requestId = `timeline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const response = await fetch(url, {
        credentials: 'include',
        headers: getApiHeadersGet(),
        cache: 'no-store' // Ensure fresh data for live updates
        // DV-TEMP-004: X-Request-Id removed due to CORS restrictions
        // Server doesn't allow this header, causing request failures
        // Request tracking still available via correlation logs
      });

      // Log request correlation for debugging and status reports
      const logEntry = {
        id: `api-${requestId}`,
        ts: new Date().toISOString(),
        level: response.ok ? 'info' : 'error',
        text: `Timeline API: GET ${url.replace(buildApiUrl(''), '')} -> ${response.status}`,
        meta: {
          source: 'timeline',
          requestId,
          responseId: response.headers.get('X-Request-Id'),
          status: response.status,
          method: 'GET',
          path: url.replace(buildApiUrl(''), ''),
          elapsed: Date.now() - parseInt(requestId.split('-')[1]) // Approximate timing
        }
      };

      console.debug('📊 Timeline API Request:', logEntry);

      if (!response.ok) {
        throw new Error(`Failed to fetch timeline events: ${response.status} (Request ID: ${requestId})`);
      }

      const data: TimelineEventsResponse = await response.json();
      return data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => lastPage?.pagination?.nextCursor,
    enabled: isComponentEnabled && Boolean(executionId) && isValidUUID(executionId), // Only enable for valid UUIDs
    refetchInterval: () => {
      // Stop polling if component is unmounted or disabled
      if (!isMountedRef.current || !isComponentEnabled) {
        return false;
      }

      return enableLiveUpdates
        ? (wsState?.isConnected ? false : 1000 + Math.random() * 300) // 1s + jitter for live mode when WS disconnected
        : (refetchInterval || 3000 + Math.random() * 300); // 3s + jitter for standard mode
    },
    staleTime: enableLiveUpdates ? 0 : 30000 // Fresh data for live mode
  });

  // Flatten and transform events with deduplication metrics
  const { events, deduplicationStats } = useMemo(() => {
    if (!data?.pages) return { events: [], deduplicationStats: { total: 0, duplicatesRemoved: 0, deduplicationMethods: {} } };

    const allEvents = data.pages.flatMap((page: any) => page?.events || []);
    const transformedEvents = allEvents.map(transformEvent);
    const totalBeforeDeduplication = transformedEvents.length;

    let duplicatesRemovedById = 0;
    let duplicatesRemovedByCorrelationId = 0;

    // DV-TEMP-004: Enhanced deduplication using both id and correlation_id
    const uniqueEvents = transformedEvents.filter((event, index, arr) => {
      const existingIndex = arr.findIndex(existingEvent => {
        // Primary deduplication by event ID
        if (existingEvent.id === event.id) {
          if (index !== existingIndex) {
            duplicatesRemovedById++;
          }
          return true;
        }

        // Secondary deduplication by correlation_id for related events
        // Only apply correlation_id deduplication if both events have correlation_id
        // and they're the same event_type from the same stage (to avoid false positives)
        if (existingEvent.correlation_id &&
            event.correlation_id &&
            existingEvent.correlation_id === event.correlation_id &&
            existingEvent.event_type === event.event_type &&
            existingEvent.stage === event.stage) {

          // Keep the more recent event when deduplicating by correlation_id
          const existingTime = new Date(existingEvent.timestamp).getTime();
          const currentTime = new Date(event.timestamp).getTime();

          if (index !== existingIndex) {
            duplicatesRemovedByCorrelationId++;
          }

          if (import.meta.env.DEV) {
            console.debug('🔄 Deduplicating events by correlation_id:', {
              kept: currentTime > existingTime ? 'current' : 'existing',
              correlation_id: event.correlation_id.slice(-8),
              event_type: event.event_type,
              stage: event.stage,
              existing_time: existingEvent.timestamp,
              current_time: event.timestamp
            });
          }

          return true;
        }

        return false;
      });

      return existingIndex === index;
    });

    const sortedEvents = uniqueEvents.sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    const totalDuplicatesRemoved = duplicatesRemovedById + duplicatesRemovedByCorrelationId;

    const stats = {
      total: totalBeforeDeduplication,
      duplicatesRemoved: totalDuplicatesRemoved,
      deduplicationMethods: {
        by_id: duplicatesRemovedById,
        by_correlation_id: duplicatesRemovedByCorrelationId
      },
      finalCount: sortedEvents.length,
      deduplicationRate: totalBeforeDeduplication > 0 ? (totalDuplicatesRemoved / totalBeforeDeduplication) : 0
    };

    if (import.meta.env.DEV && totalDuplicatesRemoved > 0) {
      console.debug('📊 Event deduplication statistics:', stats);
    }

    return { events: sortedEvents, deduplicationStats: stats };
  }, [data, transformEvent]);

  // Group events by stage for visualization
  const eventsByStage = useMemo(() => {
    const grouped = new Map<string, TimelineEvent[]>();

    events.forEach(event => {
      const stageKey = event.stage || 'unknown';
      if (!grouped.has(stageKey)) {
        grouped.set(stageKey, []);
      }
      grouped.get(stageKey)!.push(event);
    });

    return grouped;
  }, [events]);

  // Refresh timeline events (for manual refresh button)
  const refresh = useCallback(() => {
    return refetch();
  }, [refetch]);

  // Listen for WebSocket events
  // Note: Real WebSocket event handling would be implemented here
  // For now, just provide the handler for integration

  return {
    events,
    eventsByStage,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refresh,
    wsState,
    // Metrics for status reports
    totalEvents: events.length,
    eventTypeCount: events.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    stageCount: eventsByStage.size,
    lastUpdate: (data?.pages?.[0] as any)?.events?.[0]?.timestamp,
    // DV-TEMP-004: Deduplication metrics for debugging and status reports
    deduplicationStats
  };
}